import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchAllData,
  fetchData,
  fetchDeleteData,
  fetchUpdateData,
} from "../../helpers/externapi";
import "bootstrap-icons/font/bootstrap-icons.css";
import { fetchConfigValues } from "../../Commoncomponents/CommonComponents";

function CommunityPersonalDetails() {
  const { Id } = useParams();
  const navigate = useNavigate();
  const [community, setCommunity] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [hospitals, setHospitals] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("active");
  const [activeGroupTab, setActiveGroupTab] = useState("active");
  // const [groupLogo, setGroupLogo] = useState(null);
  // const [hospitalImage, setHospitalImage] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    variant: "success",
  });

  const showToast = (message, variant = "success") =>
    setToast({ show: true, message, variant });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [commRes, hospRes, grpRes] = await Promise.all([
        fetchAllData(`lambdaAPI/Community/GetById/${Id}`),
        // Fetch ALL hospitals regardless of active/inactive status to handle complex filtering
        fetchData(`lambdaAPI/CommunityHospitals/GetHospitals`, {
          communityId: parseInt(Id),
          isActive: null, // Get all hospitals
        }),
        fetchData(`lambdaAPI/CommunityGroup/FetchGroupsByCommunityId`, {
          communityId: parseInt(Id),
          isActive: null, // Get all groups
        }),
      ]);

      // const { GroupLogo, Image } = await fetchConfigValues();
      // setGroupLogo(GroupLogo);
      // setHospitalImage(Image);
      setCommunity(commRes?.[0] ?? null);
      setHospitals(hospRes ?? []);
      setGroups(grpRes ?? []);
    } catch (err) {
      console.error("Load details failed", err);
      showToast("Failed to load community details", "danger");
    } finally {
      setLoading(false);
    }
  }, [Id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (toast.show) {
      const t = setTimeout(() => setToast({ ...toast, show: false }), 3000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const navigateToHospitalDetails = (hospitalId) => {
    navigate(`/hospitals/details/${hospitalId}`, {
      state: { fromInactiveTab: activeTab === "inactive" },
    });
  };

  useEffect(() => {
      const getMocUrl = async () => {
        const { mouBucketURL, hospitalImagesURL } = await fetchConfigValues();
        setImageUrl(hospitalImagesURL);
      };
  
      getMocUrl();
    }, []);

  const navigateToGroupDetails = (groupId) => {
    navigate(`/Communities/Group/details/${groupId}`);
  };

  const requestDelete = (type, itemId) => {
    const message =
      type === "hospital"
        ? "Are you sure you want to remove this hospital from the community?"
        : "Are you sure you want to remove this group from the community?";

    setConfirm({
      message,
      action: async () => {
        try {
          const url =
            type === "hospital"
              ? `lambdaAPI/CommunityHospitals/delete/${itemId}`
              : `lambdaAPI/CommunityGroup/delete/${itemId}`;
          await fetchDeleteData(url, {}, "DELETE");
          showToast(
            `${type === "hospital" ? "Hospital" : "Group"} removed successfully`
          );
          await loadData();
        } catch (err) {
          console.error("Delete failed", err);
          showToast("Delete failed", "danger");
        } finally {
          setConfirm(null);
        }
      },
    });
  };

  const requestActivation = (type, item) => {
    const message =
      type === "hospital"
        ? "Are you sure you want to activate this hospital in the community?"
        : "Are you sure you want to activate this group in the community?";

    setConfirm({
      message,
      action: async () => {
        try {
          const updatePayload =
            type === "hospital"
              ? [
                {
                  columnName: "IsActive",
                  columnValue: 1,
                  tableName: "CommunityHospitals",
                  tableId: item.CommunityHospitalsId,
                },
              ]
              : [
                {
                  columnName: "IsActive",
                  columnValue: 1,
                  tableName: "CommunityGroup",
                  tableId: item.CommunityGroupId,
                },
              ];

          await fetchUpdateData("lambdaAPI/Update/CommonUpdate", updatePayload);

          showToast(
            `${type === "hospital" ? "Hospital" : "Group"
            } activated successfully`
          );

          await loadData();
        } catch (err) {
          console.error("Activation failed", err);
          showToast("Activation failed", "danger");
        } finally {
          setConfirm(null);
        }
      },
    });
  };

  const getHospitalStatus = (hospital) => {
    const communityHospitalActive = hospital.IsActive; // Community hospital active status
    const hospitalActive = hospital.IsHospitalActive; // Hospital active status

    return {
      showInActiveTab: communityHospitalActive && hospitalActive,
      showInInactiveTab: !communityHospitalActive || !hospitalActive,
      showActivateButton: !communityHospitalActive,
      showActivateMessage: communityHospitalActive && !hospitalActive,
      showBothActions: !communityHospitalActive && !hospitalActive,
    };
  };

  // Filter hospitals to show all hospitals but display them based on their combined status
  const getFilteredHospitals = () => {
    return hospitals.filter((hospital) => {
      const status = getHospitalStatus(hospital);
      return activeTab === "active"
        ? status.showInActiveTab
        : status.showInInactiveTab;
    });
  };

  // Get active hospitals count
  const getActiveHospitalsCount = () => {
    return hospitals.filter((hospital) => {
      const status = getHospitalStatus(hospital);
      return status.showInActiveTab;
    }).length;
  };

  // Get inactive hospitals count
  const getInactiveHospitalsCount = () => {
    return hospitals.filter((hospital) => {
      const status = getHospitalStatus(hospital);
      return status.showInInactiveTab;
    }).length;
  };

  const getGroupStatus = (group) => {
    const communityGroupActive = group.IsActive;
    const groupActive = group.IsGroupActive;

    return {
      showInActiveTab: communityGroupActive && groupActive,
      showInInactiveTab: !communityGroupActive || !groupActive,
      showActivateButton: !communityGroupActive,
      showActivateMessage: communityGroupActive && !groupActive,
      showBothActions: !communityGroupActive && !groupActive,
    };
  };

  const getFilteredGroups = () => {
    return groups.filter((group) => {
      const status = getGroupStatus(group);
      return activeGroupTab === "active"
        ? status.showInActiveTab
        : status.showInInactiveTab;
    });
  };

  // Get active groups count
  const getActiveGroupsCount = () => {
    return groups.filter((group) => {
      const status = getGroupStatus(group);
      return status.showInActiveTab;
    }).length;
  };

  // Get inactive groups count
  const getInactiveGroupsCount = () => {
    return groups.filter((group) => {
      const status = getGroupStatus(group);
      return status.showInInactiveTab;
    }).length;
  };

  // Hospital Icon Component
  const HospitalIcon = () => (
    <svg
      style={{
        width: "1.75rem",
        height: "1.75rem",
        color: "white",
      }}
      fill="currentColor"
      viewBox="0 0 24 24"
    >
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2zm-1 6v3H8v2h3v3h2v-3h3v-2h-3V8h-2z" />
    </svg>
  );

  // Group Icon Component
  const GroupIcon = () => (
    <svg
      style={{
        width: "1.75rem",
        height: "1.75rem",
        color: "white",
      }}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  );

  const filteredHospitals = getFilteredHospitals();
  const filteredGroups = getFilteredGroups();

  if (loading) {
    return (
      <div className="container py-5">
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ minHeight: "300px" }}
        >
          <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: "2rem", height: "2rem" }}
          ></div>
          <span className="ms-3">Loading community data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4 position-relative">
      <button
        className="btn btn-link mb-3 px-0"
        onClick={() => navigate("/communities")}
        style={{ textDecoration: "none" }}
      >
        <i className="bi bi-arrow-left me-1"></i> Back
      </button>

      {/* Community Info */}
      <div className="card mb-4 shadow-sm">
        <div className="card-body">
          <h3 className="card-title fw-bold mb-0">
            {community?.CommunityName ?? "Community Details"}
          </h3>
          {community?.Description && (
            <p className="text-muted mt-2 mb-0">{community.Description}</p>
          )}
        </div>
      </div>

      {/* Hospitals */}
      <section className="mb-5">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h5 className="mb-0">
            <i className="bi bi-hospital me-1 text-primary"></i> Hospitals (
            {activeTab === "active"
              ? getActiveHospitalsCount()
              : getInactiveHospitalsCount()}
            )
          </h5>
        </div>

        <div className="d-flex justify-content-center justify-content-between mb-4">
          <div className="d-flex gap-2">
            <button
              type="button"
              className={`btn ${activeTab === "active" ? "btn-primary" : "btn-outline-primary"
                }`}
              onClick={() => setActiveTab("active")}
            >
              Active 
            </button>
            <button
              type="button"
              className={`btn ${activeTab === "inactive" ? "btn-danger" : "btn-outline-danger"
                }`}
              onClick={() => setActiveTab("inactive")}
            >
              Inactive 
            </button>
          </div>
        </div>

        {filteredHospitals.length ? (
          <div className="row">
            {filteredHospitals.map((hospital) => {
              const status = getHospitalStatus(hospital);

              return (
                <div key={hospital.HospitalId} className="col-12 mb-3">
                  <div className="card shadow-sm">
                    <div className="card-body">
                      <div className="row align-items-center">
                        <div className="col-md-8">
                          <div className="d-flex align-items-start mb-2">
                            <div className="flex-grow-1">
                              {/* <h6 className="card-title mb-1 d-flex align-items-center">
                                {hospital.Image && hospitalImage ? (
                                  <img
                                    src={`${hospitalImage}/${hospital.Image}`}
                                    alt="Hospital"
                                    style={{
                                      width: "3rem",
                                      height: "3rem",
                                      borderRadius: "50%",
                                      objectFit: "cover",
                                      marginRight: "1rem",
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                      e.target.nextElementSibling.style.display = "flex";
                                    }}
                                  />
                                ) : null}
                                <div
                                  style={{
                                    width: "3rem",
                                    height: "3rem",
                                    borderRadius: "50%",
                                    backgroundColor: "#729ddeff",
                                    display: (hospital.Image && hospitalImage) ? "none" : "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginRight: "1rem",
                                  }}
                                >
                                  <HospitalIcon />
                                </div>
                                {hospital.HospitalName}
                              </h6> */}

                              <h6 className="card-title mb-1 d-flex align-items-center">
                                {hospital.Image ? (
                                  <img
                                    src={`${imageUrl}${hospital.Image}`}
                                    alt="Hospital"
                                    style={{
                                      width: "3rem",
                                      height: "3rem",
                                      borderRadius: "50%",
                                      objectFit: "cover",
                                      marginRight: "1rem",
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                      e.target.nextElementSibling.style.display = "flex";
                                    }}
                                  />
                                ) : null}
                                <div
                                  style={{
                                    width: "3rem",
                                    height: "3rem",
                                    borderRadius: "50%",
                                    backgroundColor: "#729ddeff",
                                    display: (hospital.Image && hospital.Image.trim()) ? "none" : "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginRight: "1rem",
                                  }}
                                >
                                  <HospitalIcon />
                                </div>
                                {hospital.HospitalName}
                              </h6>

                              <div className="row">
                                <div className="col-sm-6">
                                  <small className="text-muted">
                                    Hospital Code:
                                  </small>
                                  <p className="mb-1">
                                    {hospital.HospitalCode}
                                  </p>
                                </div>
                                <div className="col-sm-6">
                                  <small className="text-muted">
                                    Added on:
                                  </small>
                                  <p className="mb-1">
                                    {new Date(
                                      hospital.CreatedDate
                                    ).toLocaleDateString("en-GB", {
                                      day: "numeric",
                                      month: "short",
                                      year: "numeric",
                                    })}
                                  </p>
                                </div>
                              </div>

                              {/* Status Information - only show in Inactive tab */}
                              {activeTab === "inactive" && (
                                <div className="row mt-2">
                                  <div className="col-sm-6">
                                    <small className="text-muted">
                                      Community Hospital Status:
                                    </small>
                                    <p className="mb-1">
                                      <span
                                        className={`badge ${hospital.IsActive
                                          ? "bg-success"
                                          : "bg-danger"
                                          }`}
                                      >
                                        {hospital.IsActive
                                          ? "Active"
                                          : "Inactive"}
                                      </span>
                                    </p>
                                  </div>
                                  <div className="col-sm-6">
                                    <small className="text-muted">
                                      Hospital Status:
                                    </small>
                                    <p className="mb-1">
                                      <span
                                        className={`badge ${hospital.IsHospitalActive
                                          ? "bg-success"
                                          : "bg-danger"
                                          }`}
                                      >
                                        {hospital.IsHospitalActive
                                          ? "Active"
                                          : "Inactive"}
                                      </span>
                                    </p>
                                  </div>
                                </div>
                              )}

                              {/* Show activation message when needed - only in inactive tab */}
                              {activeTab === "inactive" &&
                                (status.showActivateMessage ||
                                  status.showBothActions) && (
                                  <div className="alert alert-warning mt-2 mb-2">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    <small>
                                      Hospital is inactive. Please activate
                                      hospital in details page.
                                    </small>
                                  </div>
                                )}

                              {hospital.HospitalAddress && (
                                <div className="mt-2">
                                  <small className="text-muted">Address:</small>
                                  <p className="mb-0">
                                    {hospital.HospitalAddress}
                                  </p>
                                </div>
                              )}
                              {(hospital.Area ||
                                hospital.Colony ||
                                hospital.Pincode) && (
                                  <div className="mt-1">
                                    <small className="text-muted">
                                      Location:
                                    </small>
                                    <p className="mb-0">
                                      {[
                                        hospital.Area,
                                        hospital.Colony,
                                        hospital.Pincode,
                                      ]
                                        .filter(Boolean)
                                        .join(", ")}
                                    </p>
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="d-flex justify-content-end gap-2 flex-wrap">
                            {/* Show activate button based on status - only in inactive tab */}
                            {activeTab === "inactive" &&
                              (status.showActivateButton ||
                                status.showBothActions) && (
                                <button
                                  className="btn btn-outline-success btn-sm"
                                  onClick={() =>
                                    requestActivation("hospital", hospital)
                                  }
                                  title="Activate Hospital in Community"
                                >
                                  <i className="bi bi-check-circle me-1"></i>{" "}
                                  Activate
                                </button>
                              )}

                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() =>
                                navigateToHospitalDetails(hospital.HospitalId)
                              }
                              title="View Hospital Details"
                            >
                              <i className="bi bi-eye me-1"></i> View Details
                            </button>

                            {/* Show delete button only when both are active - only in active tab */}
                            {activeTab === "active" &&
                              hospital.IsActive &&
                              hospital.IsHospitalActive && (
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() =>
                                    requestDelete(
                                      "hospital",
                                      hospital.CommunityHospitalsId
                                    )
                                  }
                                  title="Remove from Community"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="bi bi-hospital fs-1 text-muted mb-3"></i>
              <p className="fst-italic text-muted">
                No {activeTab === "active" ? "active" : "inactive"} hospitals
                assigned to this community.
              </p>
            </div>
          </div>
        )}
      </section>

      {/* Groups */}
      <section>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h5 className="mb-0">
            <i className="bi bi-people me-1 text-primary"></i> Groups (
            {activeGroupTab === "active"
              ? getActiveGroupsCount()
              : getInactiveGroupsCount()}
            )
          </h5>
        </div>

        <div className="d-flex justify-content-center justify-content-between mb-4">
          <div className="d-flex gap-2">
            <button
              type="button"
              className={`btn ${activeGroupTab === "active"
                ? "btn-primary"
                : "btn-outline-primary"
                }`}
              onClick={() => setActiveGroupTab("active")}
            >
              Active 
            </button>
            <button
              type="button"
              className={`btn ${activeGroupTab === "inactive"
                ? "btn-danger"
                : "btn-outline-danger"
                }`}
              onClick={() => setActiveGroupTab("inactive")}
            >
              Inactive 
            </button>
          </div>
        </div>

        {filteredGroups.length ? (
          <div className="row">
            {filteredGroups.map((group) => {
              const status = getGroupStatus(group);

              return (
                <div key={group.GroupId} className="col-12 mb-3">
                  <div className="card shadow-sm">
                    <div className="card-body">
                      <div className="row align-items-center">
                        <div className="col-md-8">
                          <div className="d-flex align-items-start mb-2">
                            <div className="flex-grow-1">
                              {/* <h6 className="card-title mb-1 d-flex align-items-center">
                                {group.GroupLogo && group.GroupLogo.trim() && groupLogo ? (
                                  <img
                                    src={`${groupLogo}/${group.GroupLogo}`}
                                    alt="Group Logo"
                                    style={{
                                      width: "3rem",
                                      height: "3rem",
                                      borderRadius: "50%",
                                      objectFit: "cover",
                                      marginRight: "1rem",
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                      e.target.nextElementSibling.style.display = "flex";
                                    }}
                                  />
                                ) : null}
                                <div
                                  style={{
                                    width: "3rem",
                                    height: "3rem",
                                    borderRadius: "50%",
                                    backgroundColor: "#729ddeff",
                                    display: (group.GroupLogo && group.GroupLogo.trim() && groupLogo) ? "none" : "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginRight: "1rem",
                                  }}
                                >
                                  <GroupIcon />
                                </div>
                                {group.GroupName || group.Name}
                              </h6> */}
                              <h6 className="card-title mb-1 d-flex align-items-center">
                                {group.GroupLogo  ? (
                                  <img
                                    src={`${imageUrl}${group.GroupLogo}`}
                                    alt="Group Logo"
                                    style={{
                                      width: "3rem",
                                      height: "3rem",
                                      borderRadius: "50%",
                                      objectFit: "cover",
                                      marginRight: "1rem",
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                      e.target.nextElementSibling.style.display = "flex";
                                    }}
                                  />
                                ) : null}
                                <div
                                  style={{
                                    width: "3rem",
                                    height: "3rem",
                                    borderRadius: "50%",
                                    backgroundColor: "#729ddeff",
                                    display: (group.GroupLogo && group.GroupLogo.trim()) ? "none" : "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginRight: "1rem",
                                  }}
                                >
                                  <GroupIcon />
                                </div>
                                {group.GroupName || group.Name}
                              </h6>
                              <div className="row">
                                <div className="col-sm-6">
                                  <small className="text-muted">
                                    Group Code:
                                  </small>
                                  <p className="mb-1">{group.Code}</p>
                                </div>
                                <div className="col-sm-6">
                                  <small className="text-muted">
                                    Added on:
                                  </small>
                                  <p className="mb-1">
                                    {new Date(
                                      group.CreatedDate
                                    ).toLocaleDateString("en-GB", {
                                      day: "numeric",
                                      month: "short",
                                      year: "numeric",
                                    })}
                                  </p>
                                </div>
                              </div>

                              {/* Status Information - only show in Inactive tab */}
                              {activeGroupTab === "inactive" && (
                                <div className="row mt-2">
                                  <div className="col-sm-6">
                                    <small className="text-muted">
                                      Community Group Status:
                                    </small>
                                    <p className="mb-1">
                                      <span
                                        className={`badge ${group.IsActive
                                          ? "bg-success"
                                          : "bg-danger"
                                          }`}
                                      >
                                        {group.IsActive ? "Active" : "Inactive"}
                                      </span>
                                    </p>
                                  </div>
                                  <div className="col-sm-6">
                                    <small className="text-muted">
                                      Group Status:
                                    </small>
                                    <p className="mb-1">
                                      <span
                                        className={`badge ${group.IsGroupActive
                                          ? "bg-success"
                                          : "bg-danger"
                                          }`}
                                      >
                                        {group.IsGroupActive
                                          ? "Active"
                                          : "Inactive"}
                                      </span>
                                    </p>
                                  </div>
                                </div>
                              )}

                              {/* Show activation message when needed - only in inactive tab */}
                              {activeGroupTab === "inactive" &&
                                (status.showActivateMessage ||
                                  status.showBothActions) && (
                                  <div className="alert alert-warning mt-2 mb-2">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    <small>
                                      Group is inactive. Please activate group
                                      in details page.
                                    </small>
                                  </div>
                                )}

                              {group.GroupAddress && (
                                <div className="mt-2">
                                  <small className="text-muted">Address:</small>
                                  <p className="mb-0">{group.GroupAddress}</p>
                                </div>
                              )}
                              {(group.Area ||
                                group.Colony ||
                                group.Pincode) && (
                                  <div className="mt-1">
                                    <small className="text-muted">
                                      Location:
                                    </small>
                                    <p className="mb-0">
                                      {[group.Area, group.Colony, group.Pincode]
                                        .filter(Boolean)
                                        .join(", ")}
                                    </p>
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="d-flex justify-content-end gap-2 flex-wrap">
                            {/* Show activate button based on status - only in inactive tab */}
                            {activeGroupTab === "inactive" &&
                              (status.showActivateButton ||
                                status.showBothActions) && (
                                <button
                                  className="btn btn-outline-success btn-sm"
                                  onClick={() =>
                                    requestActivation("group", group)
                                  }
                                  title="Activate Group in Community"
                                >
                                  <i className="bi bi-check-circle me-1"></i>{" "}
                                  Activate
                                </button>
                              )}

                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() =>
                                navigateToGroupDetails(group.GroupId)
                              }
                              title="View Group Details"
                            >
                              <i className="bi bi-eye me-1"></i> View Details
                            </button>

                            {/* Show delete button only when both are active - only in active tab */}
                            {activeGroupTab === "active" &&
                              group.IsActive &&
                              group.IsGroupActive && (
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() =>
                                    requestDelete(
                                      "group",
                                      group.CommunityGroupId
                                    )
                                  }
                                  title="Remove from Community"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="bi bi-people fs-1 text-muted mb-3"></i>
              <p className="fst-italic text-muted">
                No {activeGroupTab === "active" ? "active" : "inactive"} groups
                assigned to this community.
              </p>
            </div>
          </div>
        )}
      </section>

      {/* Confirm Modal */}
      {confirm && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1080 }}
        >
          <div
            className="card shadow"
            style={{ maxWidth: "420px", borderRadius: "6px" }}
          >
            <div className="card-body p-4">
              <h5 className="card-title">Confirm</h5>
              <p className="mb-4">{confirm.message}</p>
              <div className="d-flex justify-content-end gap-2">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setConfirm(null)}
                >
                  Cancel
                </button>
                <button className="btn btn-success" onClick={confirm.action}>
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast.show && (
        <div
          className={`toast align-items-center text-white bg-${toast.variant} border-0 position-fixed top-0 end-0 m-3 show`}
          style={{ zIndex: 1090 }}
          role="alert"
        >
          <div className="d-flex">
            <div className="toast-body">{toast.message}</div>
            <button
              type="button"
              className="btn-close btn-close-white me-2 m-auto"
              aria-label="Close"
              onClick={() => setToast({ ...toast, show: false })}
            ></button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CommunityPersonalDetails;
